import { useEffect, useState } from "react";
import { T, base, injectGlobalStyles } from "../lib/theme";
import { apiFetch } from "../lib/api";

const getStrength = (pw) => {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8)           s++;
  if (/[A-Z]/.test(pw))         s++;
  if (/[0-9]/.test(pw))         s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
};

export default function InvitePage({ onLogin }) {
  const [step,     setStep]     = useState("loading"); // loading | invalid | setup | done
  const [info,     setInfo]     = useState(null);
  const [name,     setName]     = useState("");
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  const token = new URLSearchParams(window.location.search).get("token");
  const strength = getStrength(password);

  useEffect(() => {
    injectGlobalStyles();
    if (!token) { setStep("invalid"); return; }
    apiFetch(`/api/team/invite/check?token=${token}`)
      .then(d => {
        if (d.error) { setStep("invalid"); return; }
        setInfo(d);
        setName(d.name || "");
        setStep("setup");
      })
      .catch(() => setStep("invalid"));
  }, [token]);

  const submit = async () => {
    if (!password || password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (strength < 3)                     { setError("Password is too weak"); return; }
    if (password !== confirm)             { setError("Passwords don't match"); return; }
    setLoading(true); setError("");
    try {
      const d = await apiFetch("/api/team/invite/accept", {
        method: "POST",
        body: JSON.stringify({ token, password, name: name.trim() }),
      });
      if (d.error) { setError(d.error); setLoading(false); return; }
      localStorage.setItem("token", d.token);
      onLogin(d.token);
    } catch { setError("Something went wrong — please try again"); }
    setLoading(false);
  };

  if (step === "loading") return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:T.bg0 }}>
      <div style={{ width:20, height:20, border:"2px solid rgba(255,255,255,0.1)", borderTopColor:"#6366f1", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />
    </div>
  );

  if (step === "invalid") return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:T.bg0, fontFamily:base.font }}>
      <div style={{ textAlign:"center", maxWidth:360, padding:"0 20px" }}>
        <div style={{ fontSize:40, marginBottom:16 }}>⚠️</div>
        <div style={{ fontSize:18, fontWeight:800, color:T.text1, marginBottom:8 }}>Invalid or Expired Link</div>
        <div style={{ fontSize:13, color:T.text3, lineHeight:1.6 }}>This invite link is invalid or has expired. Ask your workspace owner to send a new invite.</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:T.bg0, fontFamily:base.font, padding:"0 16px" }}>
      <div style={{ width:"100%", maxWidth:420 }}>

        {/* Logo */}
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <img src="/logo.svg" alt="Zepliq" style={{ width:64, height:64, margin:"0 auto 12px", display:"block" }} />
          <div style={{ fontSize:24, fontWeight:800, color:T.text1, letterSpacing:"-0.04em" }}>Zepliq</div>
          <div style={{ fontSize:13, color:T.text3, marginTop:4 }}>You've been invited to join a workspace</div>
        </div>

        <div style={{ background:T.bg1, borderRadius:22, border:`1px solid ${T.border2}`, padding:"26px 26px 22px", boxShadow:"0 24px 60px rgba(0,0,0,0.45)" }}>
          {/* Invite info */}
          <div style={{ padding:"12px 14px", borderRadius:12, background:"rgba(99,102,241,0.08)", border:"1px solid rgba(99,102,241,0.2)", marginBottom:20 }}>
            <div style={{ fontSize:12, color:T.text3, marginBottom:4 }}>Invited as</div>
            <div style={{ fontSize:14, fontWeight:700, color:T.text1 }}>{info?.email}</div>
            <div style={{ fontSize:11, color:"#818cf8", marginTop:2, textTransform:"capitalize" }}>Role: {info?.role}</div>
          </div>

          {error && (
            <div style={{ padding:"10px 13px", borderRadius:10, marginBottom:16, background:"rgba(244,63,94,0.1)", color:"#f43f5e", border:"1px solid rgba(244,63,94,0.2)", fontSize:13 }}>
              {error}
            </div>
          )}

          {/* Name */}
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:11, fontWeight:700, color:T.text3, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:7 }}>Your Name</label>
            <input value={name} onChange={e=>setName(e.target.value)} placeholder="Ahmed Khan"
              style={{ width:"100%", boxSizing:"border-box", padding:"11px 14px", background:T.bg0, border:`1px solid ${T.border2}`, borderRadius:10, color:T.text1, fontSize:13, outline:"none", fontFamily:base.font }}
              onFocus={e=>(e.target.style.borderColor="#6366f1")} onBlur={e=>(e.target.style.borderColor=T.border2)}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:11, fontWeight:700, color:T.text3, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:7 }}>Set Password</label>
            <div style={{ position:"relative" }}>
              <input type={showPw?"text":"password"} value={password} onChange={e=>setPassword(e.target.value)}
                placeholder="Min 8 chars, uppercase, number, symbol"
                style={{ width:"100%", boxSizing:"border-box", padding:"11px 42px 11px 14px", background:T.bg0, border:`1px solid ${T.border2}`, borderRadius:10, color:T.text1, fontSize:13, outline:"none", fontFamily:base.font }}
                onFocus={e=>(e.target.style.borderColor="#6366f1")} onBlur={e=>(e.target.style.borderColor=T.border2)}
              />
              <button onClick={()=>setShowPw(v=>!v)} type="button" style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:T.text3 }}>
                {showPw ? "👁" : "👁‍🗨"}
              </button>
            </div>
            {password && (
              <div style={{ display:"flex", gap:4, marginTop:6 }}>
                {[1,2,3,4].map(i => (
                  <div key={i} style={{ flex:1, height:3, borderRadius:2, background: i<=strength ? (strength>=3?"#10b981":strength>=2?"#f59e0b":"#f43f5e") : T.border2, transition:"background 0.2s" }} />
                ))}
              </div>
            )}
          </div>

          {/* Confirm */}
          <div style={{ marginBottom:20 }}>
            <label style={{ display:"block", fontSize:11, fontWeight:700, color:T.text3, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:7 }}>Confirm Password</label>
            <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)}
              placeholder="Re-enter password"
              style={{ width:"100%", boxSizing:"border-box", padding:"11px 14px", background:T.bg0, border:`1px solid ${confirm && confirm!==password ? "#f43f5e" : T.border2}`, borderRadius:10, color:T.text1, fontSize:13, outline:"none", fontFamily:base.font }}
              onKeyDown={e=>e.key==="Enter"&&submit()}
              onFocus={e=>(e.target.style.borderColor="#6366f1")} onBlur={e=>(e.target.style.borderColor=confirm&&confirm!==password?"#f43f5e":T.border2)}
            />
          </div>

          <button onClick={submit} disabled={loading} style={{
            width:"100%", padding:"12px", borderRadius:10, border:"none",
            background: loading ? T.bg2 : "linear-gradient(135deg,#6366f1,#4f46e5)",
            color: loading ? T.text3 : "#fff", cursor: loading ? "not-allowed" : "pointer",
            fontSize:14, fontWeight:700, fontFamily:base.font,
            boxShadow: loading ? "none" : "0 4px 20px rgba(99,102,241,0.3)",
            display:"flex", alignItems:"center", justifyContent:"center", gap:8,
          }}>
            {loading ? (
              <><div style={{ width:15,height:15,border:"2px solid rgba(255,255,255,0.25)",borderTopColor:"#fff",borderRadius:"50%",animation:"spin 0.7s linear infinite" }} />Setting up...</>
            ) : "Join Workspace →"}
          </button>
        </div>
      </div>
    </div>
  );
}
